import { useQueries, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Expand,
  KeyRound,
  LayoutGrid,
  LogOut,
  Menu,
  PanelLeftClose,
  Pin,
  PinOff,
  Search,
  Settings,
  Shrink,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { ChangePasswordDialog } from '../app/ChangePasswordDialog';
import { navItems, visibleItems } from '../app/navigation';
import { EmptyState } from '../components/EmptyState';
import { SearchInput } from '../components/SearchInput';
import { useAuth } from '../features/auth/AuthProvider';
import { useCollapsingSidebar } from '../features/monitoring/useCollapsingSidebar';
import { deviceKey, getDevice, type Device } from '../features/inventory/api';
import { getLayout, layoutKey, layoutsKey, listLayouts, toSlots } from '../features/live/layouts';
import { MosaicViewer } from '../features/live/MosaicViewer';
import { readBinding } from '../features/virtualmatrix/api';
import { useVmOpenLayout } from '../features/virtualmatrix/useVirtualMatrix';
import { VirtualMatrixDialog } from '../features/virtualmatrix/VirtualMatrixDialog';
import { useFocusTrap } from '../lib/useFocusTrap';
import { HOVER_QUERY, MD_QUERY, useMediaQuery } from '../lib/useMediaQuery';
import { useUrlState } from '../lib/useUrlState';

const LAST_KEY = 'monitoring:lastMosaic';
/** The mosaic list: the sidebar panel (desktop) or the drawer (phone). */
const PANEL_ID = 'monitoring-mosaics';

function readLast(): string {
  try {
    return localStorage.getItem(LAST_KEY) ?? '';
  } catch {
    return '';
  }
}

function writeLast(id: string) {
  try {
    localStorage.setItem(LAST_KEY, id);
  } catch {
    /* storage unavailable: the choice just is not remembered */
  }
}

/**
 * Monitoring panel (operators): the tenant's shared mosaics on the left, the
 * selected one in the full viewer on the right. No application shell.
 *
 * From the md breakpoint the list is a sidebar that folds into a thin rail
 * after SIDEBAR_IDLE_MS without interaction (pointer, keyboard focus, typing,
 * an open menu), unless the operator pins it. On phones it is a drawer over
 * the video, opened from the top bar and closed once a mosaic is chosen.
 */
export function MonitoringPage() {
  const { request, can, session, logout } = useAuth();
  const navigate = useNavigate();
  const [params, update] = useUrlState();
  const sel = params.get('m') ?? '';
  const [q, setQ] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  // Tablet and desktop: a sidebar that folds into a rail. Phone: a drawer.
  const desktop = useMediaQuery(MD_QUERY, true);
  const canHover = useMediaQuery(HOVER_QUERY, true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const asideRef = useRef<HTMLElement>(null);
  const showRef = useRef<HTMLButtonElement>(null);
  // Focus that follows a pointer press is not keyboard focus.
  const pointerFocus = useRef(false);
  // Focus we move ourselves (back to the rail) must not reopen the sidebar.
  const quietFocus = useRef(false);
  // Before folding (timer, Escape or the button): when focus was inside the
  // panel, keep it on the rail's toggle, quietly (that focus must not reopen
  // it), instead of letting it fall to the page body.
  const refocusRail = useRef(false);
  const onSidebarCollapse = () => {
    refocusRail.current = asideRef.current?.contains(document.activeElement) ?? false;
    setFocused(false);
  };
  const sidebar = useCollapsingSidebar({
    enabled: desktop,
    busy: hovered || focused || menuOpen || matrixOpen || passwordOpen,
    onCollapse: onSidebarCollapse,
  });
  // Keyboard focus that opened the rail: the control to land on once open.
  const pendingFocus = useRef<string | null>(null);
  const [fullscreen, setFullscreen] = useState(() => Boolean(document.fullscreenElement));
  // Bumped on every virtual-matrix command so the viewer remounts (fresh
  // streams) even when the same layout is pushed again — e.g. a new Situator
  // occurrence on the same zone/camera.
  const [pushNonce, setPushNonce] = useState(0);
  // The auto-pick below runs only once, after the list first loads. Never again,
  // so a layout pushed by Situator (or a viewer the operator closed) is not
  // overwritten by re-selecting the last-used mosaic.
  const autoSelected = useRef(false);

  // A virtual-matrix command for THIS screen's workstation switches the mosaic
  // (ADR-039). The command carries the guid as "{UPPER}"; the binding keeps the
  // lowercase uuid. A null/empty layout means "clear the screen".
  useVmOpenLayout((cmd) => {
    const binding = readBinding();
    if (!binding) return;
    const target = cmd.workstationGuid.replace(/[{}]/g, '').toLowerCase();
    if (target !== binding.id.toLowerCase()) return;
    setPushNonce((n) => n + 1);
    // Filter the sidebar to the called mosaic (clear it when the screen is cleared).
    setQ(cmd.layoutId ? cmd.layoutName : '');
    update({ m: cmd.layoutId ?? '' });
  });

  const layouts = useQuery({
    queryKey: layoutsKey,
    queryFn: () => listLayouts(request),
    refetchInterval: 15_000,
  });
  const shared = (layouts.data ?? []).filter((l) => l.shared);
  const needle = q.trim().toLowerCase();
  const filtered = shared.filter((l) => l.name.toLowerCase().includes(needle));

  const current = shared.find((l) => l.id === sel);

  // Land on the last mosaic used here, or else the first shared one. This also
  // recovers from a stale or unshared ?m= (deleted mosaic, old bookmark).
  const firstId = shared[0]?.id ?? '';
  const lastId = shared.find((l) => l.id === readLast())?.id ?? '';
  const pick = lastId || firstId;
  useEffect(() => {
    if (autoSelected.current || !layouts.data) return;
    autoSelected.current = true;
    if (!current && pick) update({ m: pick });
  }, [current, pick, update, layouts.data]);

  // Remember whatever is showing (clicked, auto-selected or linked).
  const currentId = current?.id ?? '';
  useEffect(() => {
    if (currentId) writeLast(currentId);
  }, [currentId]);
  // Bring the active mosaic into view when it changes (also when Situator
  // pushes one), so the operator sees which one is on screen.
  // Also when the sidebar opens again.
  const listShown = desktop ? sidebar.expanded : drawerOpen;
  useEffect(() => {
    if (!sel || !listShown) return;
    document.getElementById(`mosaic-opt-${sel}`)?.scrollIntoView({ block: 'nearest' });
  }, [sel, listShown]);

  // Once the rail is rendered, take the focus that was in the panel.
  useLayoutEffect(() => {
    if (sidebar.expanded || !refocusRail.current) return;
    refocusRail.current = false;
    quietFocus.current = true;
    showRef.current?.focus();
    if (document.activeElement !== showRef.current) quietFocus.current = false;
  }, [sidebar.expanded]);

  // Keyboard focus that opened the rail lands on the same control in the panel.
  useEffect(() => {
    if (!sidebar.expanded || !pendingFocus.current) return;
    const key = pendingFocus.current;
    pendingFocus.current = null;
    const root = asideRef.current;
    const target =
      key === 'filter'
        ? root?.querySelector<HTMLElement>('input')
        : root?.querySelector<HTMLElement>(`[data-focus-key="${key}"]`);
    target?.focus();
  }, [sidebar.expanded]);

  const collapseToRail = sidebar.collapse;
  const choose = (id: string) => {
    update({ m: id });
  };
  const closeViewer = () => {
    update({ m: '' });
  };

  const layout = useQuery({
    queryKey: layoutKey(sel),
    queryFn: () => getLayout(request, sel),
    enabled: Boolean(current),
  });
  const slots = layout.data ? toSlots(layout.data.tiles) : [];
  const ids = [...new Set(slots.filter((s): s is string => s !== null))];
  const devices = useQueries({
    queries: ids.map((d) => ({
      queryKey: deviceKey(d),
      queryFn: () => getDevice(request, d),
      retry: false,
    })),
  });
  const byId = new Map<string, Device>();
  for (const r of devices) if (r.data) byId.set(r.data.id, r.data);
  const cameras = ids.flatMap((d) => {
    const dev = byId.get(d);
    return dev ? [{ id: dev.id, name: dev.name }] : [];
  });

  useEffect(() => {
    document.title = 'Monitoramento · Pro Guard';
  }, []);
  useEffect(() => {
    const onChange = () => {
      setFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen().catch(() => undefined);
  };
  const signOut = async () => {
    try {
      await logout();
    } finally {
      void navigate('/login', { replace: true });
    }
  };

  const iconButton =
    'grid size-8 shrink-0 place-items-center rounded-md text-fg-muted transition-colors hover:bg-surface-raised hover:text-fg';

  // Whoever also has other screens (opened this from the Operação menu) gets
  // a way back: the installed app (PWA) has no address bar. An operator who
  // only monitors has nowhere else to go.
  const backToSystem = visibleItems(
    navItems,
    can,
    Boolean(session?.user.client_id),
    Boolean(session?.tenant.platform),
  ).some((i) => i.to !== '/monitoring');

  // Header: organisation and the screen controls. `focusKey` lets keyboard
  // focus that opened the rail land on the matching control.
  const header = (closeDrawer?: () => void) => (
    <header className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1">
        {backToSystem && (
          <button
            type="button"
            data-focus-key="back"
            aria-label="Voltar ao sistema"
            title="Voltar ao sistema"
            onClick={() => {
              closeDrawer?.();
              void navigate('/');
            }}
            className={iconButton}
          >
            <ArrowLeft className="size-4" />
          </button>
        )}
        <span className="min-w-0 truncate text-sm font-semibold text-fg">
          {session?.tenant.name}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button
          type="button"
          data-focus-key="fullscreen"
          aria-label={fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
          onClick={toggleFullscreen}
          className={iconButton}
        >
          {fullscreen ? <Shrink className="size-4" /> : <Expand className="size-4" />}
        </button>
        <div className="relative">
          <button
            type="button"
            aria-label="Configurações"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen((o) => !o);
            }}
            className={iconButton}
          >
            <Settings className="size-4" />
          </button>
          {menuOpen && (
            <>
              <div
                aria-hidden="true"
                className="fixed inset-0 z-30"
                onClick={() => {
                  setMenuOpen(false);
                }}
              />
              <div
                role="menu"
                className="absolute right-0 top-9 z-40 w-52 overflow-hidden rounded-md border border-line bg-surface-raised py-1 shadow-xl shadow-black/40"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    closeDrawer?.();
                    setPasswordOpen(true);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-fg-muted hover:bg-surface hover:text-fg"
                >
                  <KeyRound className="size-4" />
                  Alterar senha
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    closeDrawer?.();
                    setMatrixOpen(true);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-fg-muted hover:bg-surface hover:text-fg"
                >
                  <SlidersHorizontal className="size-4" />
                  Configurar matriz virtual
                </button>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          data-focus-key="logout"
          aria-label="Sair"
          onClick={() => void signOut()}
          className={iconButton}
        >
          <LogOut className="size-4" />
        </button>
        {closeDrawer && (
          <button
            type="button"
            aria-label="Fechar lista de mosaicos"
            onClick={closeDrawer}
            className={iconButton}
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    </header>
  );

  // Filter, list and empty state: the same in the sidebar and the drawer.
  const mosaicList = (afterChoose?: () => void) => (
    <>
      <SearchInput
        label="Filtrar mosaicos por nome"
        placeholder="Filtrar mosaicos"
        value={q}
        onChange={setQ}
      />
      <ul
        role="listbox"
        aria-label="Mosaicos compartilhados"
        className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overscroll-contain"
      >
        {filtered.map((l) => (
          <li key={l.id} role="presentation" className="flex items-center gap-1">
            <button
              id={`mosaic-opt-${l.id}`}
              type="button"
              role="option"
              aria-selected={l.id === sel}
              onClick={() => {
                choose(l.id);
                afterChoose?.();
              }}
              className={`min-w-0 flex-1 truncate rounded-md px-2.5 py-2 text-left text-[13px] max-md:py-2.5 ${
                l.id === sel
                  ? 'bg-accent-soft text-fg'
                  : 'text-fg-muted hover:bg-surface-raised hover:text-fg'
              }`}
            >
              {l.name}
            </button>
            {l.id === sel && can('monitoring.close') && (
              <button
                type="button"
                aria-label={`Fechar ${l.name}`}
                title="Fechar imagens"
                onClick={closeViewer}
                className="grid size-8 shrink-0 place-items-center rounded-md text-fg-muted hover:bg-surface-raised hover:text-fg"
              >
                <X className="size-4" />
              </button>
            )}
          </li>
        ))}
      </ul>
      {layouts.data && filtered.length === 0 && (
        <EmptyState
          icon={LayoutGrid}
          title="Nenhum mosaico compartilhado"
          description={
            shared.length === 0
              ? 'Peça a um administrador para criar e compartilhar um mosaico.'
              : 'Nenhum mosaico corresponde ao filtro.'
          }
        />
      )}
    </>
  );

  const viewer =
    current && layout.data ? (
      <MosaicViewer
        key={`${layout.data.id}:${pushNonce}`}
        name={layout.data.name}
        grid={layout.data.grid}
        slots={slots}
        devices={byId}
        cameras={cameras}
        canReplay={can('recordings.view') || can('monitoring.view')}
        active
        timeZone={session?.tenant.timezone}
        dockedBarInFullscreen
        fullscreenButton={false}
      />
    ) : current ? (
      <div role="status" className="grid h-full place-items-center">
        <span
          aria-hidden="true"
          className="size-5 animate-spin rounded-full border-2 border-line-strong border-t-accent"
        />
        <span className="sr-only">Abrindo mosaico…</span>
      </div>
    ) : (
      <div className="grid h-full place-items-center p-4">
        <EmptyState
          icon={LayoutGrid}
          title="Selecione um mosaico"
          description={
            desktop
              ? 'Escolha um mosaico compartilhado na lista ao lado.'
              : 'Toque em Mosaicos para escolher um mosaico compartilhado.'
          }
        />
      </div>
    );

  const dialogs = (
    <>
      {matrixOpen && (
        <VirtualMatrixDialog
          onClose={() => {
            setMatrixOpen(false);
          }}
        />
      )}
      {passwordOpen && (
        <ChangePasswordDialog
          onClose={() => {
            setPasswordOpen(false);
          }}
        />
      )}
    </>
  );

  // The page fills the dynamic viewport (mobile toolbars) and keeps clear of
  // the notch and home indicator when installed as an app.
  const shell =
    'flex h-dvh overflow-hidden bg-canvas pt-[env(safe-area-inset-top)] pr-[env(safe-area-inset-right)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]';

  if (!desktop) {
    return (
      <div className={`${shell} flex-col`}>
        <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-2">
          <button
            type="button"
            aria-label="Mostrar mosaicos"
            aria-expanded={drawerOpen}
            aria-controls={drawerOpen ? PANEL_ID : undefined}
            onClick={() => {
              setDrawerOpen(true);
            }}
            className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md border border-line bg-canvas px-2.5 text-[13px] font-medium text-fg hover:bg-surface-raised"
          >
            <Menu aria-hidden="true" className="size-4" />
            Mosaicos
          </button>
          <span className="min-w-0 flex-1 truncate text-[13px] text-fg-muted">
            {current?.name ?? session?.tenant.name}
          </span>
        </div>
        <main className="min-h-0 min-w-0 flex-1 overflow-hidden">{viewer}</main>
        {drawerOpen && (
          <MosaicDrawer
            onClose={() => {
              setDrawerOpen(false);
            }}
          >
            {header(() => {
              setDrawerOpen(false);
            })}
            {mosaicList(() => {
              setDrawerOpen(false);
            })}
          </MosaicDrawer>
        )}
        {dialogs}
      </div>
    );
  }

  const railButton =
    'grid size-9 place-items-center rounded-md text-fg-muted transition-colors hover:bg-surface-raised hover:text-fg';

  return (
    <div className={shell}>
      <aside
        ref={asideRef}
        aria-label="Mosaicos"
        data-state={sidebar.expanded ? 'expanded' : 'collapsed'}
        // Opens only by a click on its icons (0.68.5): the rail's own buttons
        // (full screen, logout) must work without the panel jumping open.
        // Hovering the open panel holds it open.
        onPointerEnter={(e) => {
          if (!canHover || e.pointerType !== 'mouse' || !sidebar.expanded) return;
          setHovered(true);
        }}
        onPointerLeave={() => {
          setHovered(false);
        }}
        // Taps, wheel and list scrolling keep it open (touch has no hover).
        onWheel={sidebar.touch}
        onScrollCapture={sidebar.touch}
        onPointerDown={() => {
          sidebar.touch();
          pointerFocus.current = true;
          window.setTimeout(() => {
            pointerFocus.current = false;
          }, 0);
        }}
        onFocus={(e) => {
          if (quietFocus.current) {
            quietFocus.current = false;
            return;
          }
          const keyboard = !pointerFocus.current;
          // Typing in the filter, or moving around by keyboard, holds it open;
          // a mouse click on a mosaic does not (the pointer leaving restarts it).
          setFocused(sidebar.expanded && (keyboard || e.target instanceof HTMLInputElement));
        }}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
        }}
        onKeyDown={(e) => {
          if (e.key !== 'Escape' || e.defaultPrevented) return;
          if (menuOpen) {
            setMenuOpen(false);
            return;
          }
          if (sidebar.pinned || !sidebar.expanded) return;
          collapseToRail();
        }}
        className={`flex min-h-0 shrink-0 flex-col overflow-hidden border-r border-line bg-surface transition-[width] duration-200 ease-out motion-reduce:transition-none ${
          sidebar.expanded ? 'w-72 gap-3 p-3' : 'w-12 items-center gap-1 px-1.5 py-2'
        }`}
      >
        {sidebar.expanded ? (
          <div id={PANEL_ID} className="flex min-h-0 w-[16.5rem] flex-1 flex-col gap-3">
            {header()}
            <div className="flex items-center gap-1">
              <h2 className="flex-1 text-2xs font-semibold tracking-wider text-fg-subtle uppercase">
                Mosaicos
              </h2>
              <button
                type="button"
                aria-pressed={sidebar.pinned}
                aria-label={sidebar.pinned ? 'Soltar lateral' : 'Fixar lateral'}
                title={sidebar.pinned ? 'Soltar lateral' : 'Fixar lateral'}
                onClick={sidebar.togglePin}
                className={`${iconButton} ${sidebar.pinned ? 'bg-accent-soft text-accent hover:text-accent' : ''}`}
              >
                {sidebar.pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
              </button>
              <button
                type="button"
                aria-label="Ocultar mosaicos"
                aria-expanded="true"
                aria-controls={PANEL_ID}
                title="Ocultar mosaicos"
                onClick={collapseToRail}
                className={iconButton}
              >
                <PanelLeftClose className="size-4" />
              </button>
            </div>
            {mosaicList()}
          </div>
        ) : (
          <>
            <button
              ref={showRef}
              type="button"
              data-focus-key="filter"
              aria-label="Mostrar mosaicos"
              aria-expanded="false"
              title="Mostrar mosaicos"
              onClick={sidebar.expand}
              className={`${railButton} text-fg`}
            >
              <LayoutGrid className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Buscar mosaico"
              aria-expanded="false"
              title="Buscar mosaico"
              onClick={() => {
                pendingFocus.current = 'filter';
                sidebar.expand();
              }}
              className={railButton}
            >
              <Search className="size-4" />
            </button>
            <span aria-hidden="true" className="my-1 h-px w-6 bg-line" />
            <button
              type="button"
              data-focus-key="fullscreen"
              aria-label={fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              title={fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
              onClick={toggleFullscreen}
              className={railButton}
            >
              {fullscreen ? <Shrink className="size-4" /> : <Expand className="size-4" />}
            </button>
            <button
              type="button"
              data-focus-key="logout"
              aria-label="Sair"
              title="Sair"
              onClick={() => void signOut()}
              className={`${railButton} mt-auto`}
            >
              <LogOut className="size-4" />
            </button>
          </>
        )}
      </aside>
      <main className="min-h-0 min-w-0 flex-1 overflow-hidden">{viewer}</main>
      {dialogs}
    </div>
  );
}

/** The mosaic list over the video on phones: modal, focus-trapped. */
function MosaicDrawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, onClose);
  return (
    <div className="fixed inset-0 z-40">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={ref}
        id={PANEL_ID}
        role="dialog"
        aria-modal="true"
        aria-label="Mosaicos"
        tabIndex={-1}
        className="relative flex h-full w-[min(20rem,85vw)] flex-col gap-3 border-r border-line-strong bg-surface p-3 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-[max(0.75rem,env(safe-area-inset-left))] shadow-2xl shadow-black/50 focus:outline-none"
      >
        {children}
      </div>
    </div>
  );
}
